import { ArrowDownIcon, Building2Icon, Layers2Icon, PipetteIcon, PlusIcon, TriangleIcon } from "lucide-react";
import { cn } from "@/utils/class-name";

interface ComposedLucideIconProps {
    className?: string;
    "aria-hidden"?: boolean;
}

export type ComposedLucideIconComponent = React.FC<ComposedLucideIconProps>;

/**
 * These are components that are 2 or more Lucide icons combined to create a single icon
 *
 * Make sure to pass through the className prop that is provided by icon component
 */

export function AddOrganizationIcon({ className, "aria-hidden": ariaHidden }: ComposedLucideIconProps) {
    return (
        <div className={cn("relative flex items-center justify-center", className)}>
            <Building2Icon className={"size-[90%] relative right-[5%]"} aria-hidden={ariaHidden} />
            <PlusIcon className={"absolute -right-[15%] -top-[10%] size-[45%] stroke-3"} aria-hidden={ariaHidden} />
        </div>
    );
}

/** Stands in for Lucide's `layers-arrow-down`, which the installed lucide-react (0.548) doesn't ship. */
export function AddLayerBelowIcon({ className, "aria-hidden": ariaHidden }: ComposedLucideIconProps) {
    return (
        <div className={cn("relative flex items-center justify-center", className)}>
            <Layers2Icon className={"relative right-[8%] size-[85%]"} aria-hidden={ariaHidden} />
            <ArrowDownIcon
                className={"-right-[12%] -bottom-[8%] absolute size-[50%] stroke-3"}
                aria-hidden={ariaHidden}
            />
        </div>
    );
}

/**
 * A pipette over a triangle: picking a soil texture off the texture triangle, as a colour off a swatch.
 * The triangle fills the box; the pipette crosses its right side, tip in, in the primary colour, over a
 * knockout so the triangle's line doesn't run through it. The knockout is `--toggle-fill` when the
 * button sets it to its own fill, else the outline button's popover fill.
 */
export function SoilTexturePickerIcon({ className, "aria-hidden": ariaHidden }: ComposedLucideIconProps) {
    const pipette = "-right-[14%] -top-[12%] absolute size-[72%]";
    return (
        <div className={cn("relative flex items-center justify-center", className)}>
            <TriangleIcon className={"size-full"} aria-hidden={ariaHidden} />
            <PipetteIcon
                className={cn(pipette, "text-(--toggle-fill,var(--color-popover))")}
                strokeWidth={6}
                aria-hidden={ariaHidden}
            />
            <PipetteIcon className={cn(pipette, "text-primary")} aria-hidden={ariaHidden} />
        </div>
    );
}
